import {
  NONE_ORDER_KEY,
  NONE_ORDER_DISPLAY_NAME,
  STORE_CREW,
  IS_CREATED_BY_SCAN,
  EVENT_PERSON_SCAN,
  ORDER_SUBMITTED_STATUS,
  ORDER_PAID_FLAG,
} from "../Config"
import * as api from "./api"
import { getScanEventTime } from "./events"
import { isRestockerCartId } from "../actions/cart"

export const SHELF_RESTOCKER_TRACK_ID = "shelf-restocker-track"
export const AUTO_TAGGED_STORE_CREW_ID = "auto-tagged-as-store-crew"
export const MISSING_CART_ID = "Missing"
export const UNASSIGNED_CART = "Unassigned"
export const VIRTUAL_CART_IDS = [
  UNASSIGNED_CART,
  SHELF_RESTOCKER_TRACK_ID,
  AUTO_TAGGED_STORE_CREW_ID,
  MISSING_CART_ID,
]

/**
 * Returns true if the order object is a family order (has familyOrder key)
 * @param {object} order
 * @returns {boolean}
 */
export const isFamilyOrder = (order) => {
  if (typeof order !== "object") {
    return false
  }
  return Object.prototype.hasOwnProperty.call(order, "familyOrder")
}

/**
 * Returns true if @param event is resolved to @param orderId
 * @param {object} event
 * @param {string} orderId
 * @returns {boolean}
 */
export const isEventResolvedToOrder = (event, orderId) => {
  return event && event.resolved_track === orderId
}

/**
 * Returns true if @param orderId is a candidate for @param event
 * @param {object} event
 * @param {string} orderId
 * @returns {boolean}
 */
export const isOrderACandidateOfEvent = (event, orderId) => {
  return (
    event &&
    typeof event["candidate-tracks"] === "string" &&
    event["candidate-tracks"].includes(orderId)
  )
}

/**
 * Returns true if @param event is resolved to any order in the family order @param order
 * @param {object} event
 * @param {object} order
 * @returns {boolean}
 */
export const isEventAssignedToFamily = (event, order) => {
  if (!event || !order || !isFamilyOrder(order)) {
    return false
  }
  let result = false
  if (typeof order?.familyOrder?.orders === "object") {
    result = Object.keys(order.familyOrder.orders).some((orderId) => {
      return event["resolved_track"] === orderId || event["cart"] === orderId
    })
  }
  return result
}

/**
 * Returns true if @param event is resolved to any order
 * @param {object} event
 * @returns {boolean}
 */
export const isEventMappedToAnOrder = (event) => {
  if (!event) {
    return false
  }
  const cartId = event["resolved_track"] || event["cart"]
  if (!cartId || VIRTUAL_CART_IDS.includes(cartId) || event.is_crew_event) {
    return false
  }
  return true
}

/**
 * Returns true if any order in the family order @param order is a candidate for @param event
 * @param {object} event
 * @param {object} order
 * @returns {boolean}
 */
export const isFamilyCandidateOfEvent = (event, order) => {
  if (!event || typeof event["candidate-tracks"] !== "string" || !order || !isFamilyOrder(order)) {
    return false
  }
  let result = false
  if (typeof order?.familyOrder?.orders === "object") {
    result = Object.keys(order.familyOrder.orders).some((orderId) => {
      return event["candidate-tracks"].includes(orderId)
    })
  }
  return result
}

/**
 * Returns true if @param event is mapped to @param orderId
 * @param {object} event
 * @param {string} orderId
 * @returns {boolean}
 */
export const isEventAssignedToOrder = (event, orderId) => {
  return event?.resolved_track === orderId || event?.cart === orderId
}

export const isPersonScanCart = (order) => {
  if (typeof order !== "object") {
    return false
  }
  if (order[IS_CREATED_BY_SCAN] === "1") {
    return true
  }
  if (Object.prototype.hasOwnProperty.call(order, "familyOrder")) {
    const orders = order.familyOrder?.orders
    if (orders) {
      const parentCart = getParentCart(orders)
      if (parentCart && parentCart[IS_CREATED_BY_SCAN] === "1") {
        return true
      }
    }
  }
  return false
}

const getParentCart = (orders) => {
  for (const orderId in orders) {
    const order = orders[orderId]
    if (order["order_type"] === "parent") {
      return order
    }
  }
  return null
}

export const getEventTimestampAndCam = async (event) => {
  if (!event) {
    return null
  }
  let eventCam = event.cam_id
  let eventTime = event.start ?? event.end
  if (event.type === EVENT_PERSON_SCAN) {
    if (!eventCam) {
      eventCam = await getPersonScanEventCam(event)
      event.cam_id = eventCam
    }
    eventTime = getScanEventTime(event)
  }
  return { eventTime, eventCam }
}

export const getPersonScanEventCam = async (event) => {
  if (!event) return null
  if (event.cam_id) return event.cam_id
  const eventSrc = event.source
  if (eventSrc) {
    const camDetails = await api.getEntryCamera(eventSrc)
    return camDetails?.camera
  }
  return null
}

export const getDisplayCartId = (cartId, options = {}) => {
  const { abbreviated = false, isCrewEvent = false } = options

  // ---- Validate input ----
  if (typeof cartId !== "string") return ""

  const normalized = cartId.trim()
  if (!normalized) return ""

  const lower = normalized.toLowerCase()

  // ---- Empty / invalid carts ----
  if (normalized === UNASSIGNED_CART || normalized === MISSING_CART_ID || lower === "none") {
    return ""
  }

  // ---- Store crew carts ----
  if (normalized === AUTO_TAGGED_STORE_CREW_ID || normalized === "Store Crew") {
    return "crew"
  }

  // ---- Restocker carts ----
  if (isRestockerCartId(normalized)) {
    return abbreviated ? "restock" : "rear-loading"
  }

  // ---- Default: last 4 chars ----
  const baseId = normalized.slice(-4)

  // ---- Optional suffix for crew carts----
  return `${baseId}${isCrewEvent ? " (Store Crew)" : ""}`
}

export const isNoneOrStoreCrewOrder = (orderId) => {
  return orderId === NONE_ORDER_DISPLAY_NAME || orderId === NONE_ORDER_KEY || orderId === STORE_CREW
}

export const shouldFetchOrderDetails = (orderId, ordersDetails) => {
  // don't get order details if state already has them
  if (ordersDetails && ordersDetails[orderId]) {
    return false
  }
  return !isNoneOrStoreCrewOrder(orderId)
}

export const getOrderDetails = async (orderId) => {
  console.log("getOrderDetails(orderId)", orderId)
  try {
    let orderEvents = await api.getOrderEvents(orderId)
    console.log("TaskDetails: orderEvents", JSON.stringify(orderEvents))
    if (Array.isArray(orderEvents.sortedIds)) {
      orderEvents.sortedIds.sort(function (a, b) {
        // Turn your strings into dates, and then subtract them
        // to get a value that is either negative, positive, or zero.
        // TODO: Better handle when an eventId is on order, but no event details were
        //  found
        if (!orderEvents.objects[a] || !orderEvents.objects[b]) {
          return 0
        }
        return orderEvents.objects[a].start - orderEvents.objects[b].start
      })
    }
    console.log("TaskDetails: orderEvents after sort", orderEvents)
    let order = await api.getOrder(orderId)
    return {
      ...order,
      orderEvents,
    }
  } catch (e) {
    console.log("Error getting selected order details", e)
  }
}

export const getRecentOngoingOrders = (storeId, endTimestamp, eventEndTimeStamp) => {
  // End is sixty minutes in future to accomodate order score update. (prev. one minute)
  const end = endTimestamp + 60 * 60 * 1000
  const start = endTimestamp - 1 * 60 * 60 * 1000 // One hour back.
  return api.getRecentOngoingOrders(storeId, end, start, eventEndTimeStamp)
}

export const isOrderSubmitted = (order) => {
  return order?.status === ORDER_SUBMITTED_STATUS || order?.Paid === ORDER_PAID_FLAG
}

export const isCartEvent = (event, orderId, order) => {
  return isEventAssignedToOrder(event, orderId) || isEventAssignedToFamily(event, order)
}
